/**
 * API facade — composes the per-feature endpoint groups into the single
 * `api` object that every page consumes. The original god-file was split
 * along the same feature axis as the backend routers (see `backend/app/api/`).
 *
 * Consumers should still `import { api, ... } from './api/client'` — this
 * module re-exports types, core helpers, and the composed `api` so nothing
 * downstream had to change when the split happened.
 */

import { aceApi } from './ace'
import { adminApi } from './admin'
import { analysisApi } from './analysis'
import { assessmentsApi } from './assessments'
import { assistantApi } from './assistant'
import { awsImportApi } from './aws-import'
import { clarificationsApi } from './clarifications'
import { companiesApi } from './companies'
import { generateApi } from './generate'
import { intakeApi } from './intake'
import { proposalsApi } from './proposals'
import { researchApi } from './research'
import { settingsApi } from './settings'
import { signoffApi } from './signoff'
import { submissionApi } from './submission'
import { transcriptReviewApi } from './transcriptReview'
import { wafrReviewApi } from './wafrReview'

// Re-export core HTTP helpers + the StaleStateError sentinel so existing
// callers ({ authHeaders, handleUnauthorized, StaleStateError } from './client')
// don't break.
export { authHeaders, handleUnauthorized, StaleStateError, streamSSE } from './core'

// Re-export every type used by pages.
export type {
  AceCandidate,
  AceSearchResponse,
  AgentCoreFeasibility,
  AgentProposal,
  AnalysisDimension,
  ArchitectureStatus,
  AnalysisResponse,
  AnalysisResult,
  AnalysisSuggestion,
  AssessmentMeta,
  AssessmentState,
  AssessmentSummary,
  AssistantChange,
  AssistantContext,
  AssistantMessage,
  AssistantResponse,
  AssistantTarget,
  Company,
  CompanyMeta,
  CompanySummary,
  ModuleCompletion,
  ModuleQuestionnaire,
  ModuleSummary,
  AwsAccountOutput,
  AwsAccountPayload,
  AwsConnectionMode,
  AwsConnectionOutput,
  AwsConnectionPayload,
  AwsPlatformIdentity,
  Clarification,
  ClarifyListResponse,
  ConnMethod,
  FieldMeta,
  JobStatus,
  Message,
  PendingFinding,
  ProposalPatch,
  ProposalSuggestion,
  QuickFeasibility,
  ResearchResponse,
  ROIInputs,
  TranscriptReviewResponse,
  TranscriptSummary,
  WafrClarification,
  WafrEvidenceRef,
  WafrFundingRequest,
  WafrItem,
  WafrItemAnswer,
  WafrItemCheck,
  WafrItemDraft,
  WafrRemediation,
  WafrReviewMeta,
  WafrReviewState,
  WafrSessionMessage,
  WafrSessionState,
} from './types'

export type {
  AuditAssessment,
  AuditChange,
  AuditEntry,
  AuditPage,
  PlaybookEvidenceCall,
  PlaybookPlay,
  PlaybooksResponse,
} from './admin'
export type { DeliverableFile, PresentationStatus } from './generate'
export type { AIProposalsSnapshot, UseCaseHypothesis } from './proposals'
export type { SettingDef, SettingsResponse } from './settings'
export type { SignoffSummary } from './signoff'

export const api = {
  ...aceApi,
  ...adminApi,
  ...analysisApi,
  ...assessmentsApi,
  ...assistantApi,
  ...intakeApi,
  ...researchApi,
  ...proposalsApi,
  ...clarificationsApi,
  ...awsImportApi,
  ...companiesApi,
  ...generateApi,
  ...submissionApi,
  ...transcriptReviewApi,
  ...settingsApi,
  ...signoffApi,
  ...wafrReviewApi,
}
